import React from "react";
import "./or-checkbox.css";

export type OrCheckboxTheme = "default" | "social";

export interface OrCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: React.ReactNode;
  indeterminate?: boolean;
  label?: React.ReactNode;
  theme?: OrCheckboxTheme;
}

function isPlainTextLabel(label: React.ReactNode): label is string | number {
  return typeof label === "string" || typeof label === "number";
}

export const OrCheckbox = React.forwardRef<HTMLInputElement, OrCheckboxProps>(
  ({ label, error, className = "", id, indeterminate = false, theme = "default", ...props }, ref) => {
    const reactId = React.useId();
    const checkboxId = id ?? `checkbox-${reactId.replace(/:/g, "")}`;
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const setInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;

        if (node) {
          node.indeterminate = indeterminate;
        }

        if (typeof ref === "function") {
          ref(node);
          return;
        }

        if (ref) {
          ref.current = node;
        }
      },
      [indeterminate, ref],
    );

    const handleLabelContentClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (props.disabled) {
          return;
        }

        if (event.target instanceof Element && event.target.closest("a, button, input, select, textarea, summary")) {
          return;
        }

        inputRef.current?.click();
      },
      [props.disabled],
    );

    const containerClassName = [
      "or-checkbox-container",
      theme === "social" ? "or-checkbox-container--social" : "",
      props.disabled ? "or-checkbox-container--disabled" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={containerClassName}>
        <div className="or-checkbox-row">
          <label htmlFor={checkboxId} className="or-checkbox-label">
            <div className="or-checkbox-wrapper">
              <input type="checkbox" id={checkboxId} ref={setInputRef} className="or-checkbox-input" {...props} />
              <span className="or-checkbox-custom">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="or-checkbox-icon"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="or-checkbox-icon or-checkbox-icon--indeterminate"
                >
                  <line x1="6" y1="12" x2="18" y2="12" />
                </svg>
              </span>
            </div>
          </label>

          {label ? (
            isPlainTextLabel(label) ? (
              <label className="or-checkbox-text" htmlFor={checkboxId}>
                {label}
              </label>
            ) : (
              <div className="or-checkbox-text" onClick={handleLabelContentClick}>
                {label}
              </div>
            )
          ) : null}
        </div>
        {error && <span className="or-checkbox-error">{error}</span>}
      </div>
    );
  },
);

OrCheckbox.displayName = "OrCheckbox";
